import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { freeComics } from '../data/comics';
import { loadComicPage } from '../utils/imageDecrypt';
import { Maximize2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';

export default function FreeComicViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  const [currentPage, setCurrentPage] = useState(0);
  const [isZoomMode, setIsZoomMode] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [decryptedImageUrl, setDecryptedImageUrl] = useState(null);
  const [isDecrypting, setIsDecrypting] = useState(true);
  const imageRef = useRef(null);
  const lastTouchDistance = useRef(0);

  const comic = freeComics.find((c) => c.id === id);
  const pages = useMemo(() => comic?.pages || [], [comic]);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsZoomMode(false);
  }, [currentPage]);

  // Decrypt current page image
  useEffect(() => {
    if (!comic || pages.length === 0) return;
    
    const loadDecryptedImage = async () => {
      setIsDecrypting(true);
      try {
        const originalUrl = pages[currentPage].imageUrl;
        const decryptedUrl = await loadComicPage(originalUrl);
        setDecryptedImageUrl(decryptedUrl);
      } catch (error) {
        console.warn('Page load failed:', error);
        setDecryptedImageUrl(null);
      }
      setIsDecrypting(false);
    };
    
    loadDecryptedImage();
  }, [comic, currentPage, pages]);

  const handleNext = () => {
    if (comic && currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  const toggleZoom = () => {
    if (isZoomMode) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
    setIsZoomMode(!isZoomMode);
  };

  if (!comic) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <div style={styles.errorIcon}>📚</div>
          <h2 style={styles.errorTitle}>{t('comicNotFound')}</h2>
          <p style={styles.errorText}>{t('comicNotFoundDesc')}</p>
          <button onClick={() => navigate('/')} style={styles.backButton}>
            {t('backToHome')}
          </button>
        </div>
      </div>
    );
  }

  const handleTouchStart = (e) => {
    if (!isZoomMode) return;

    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      lastTouchDistance.current = distance;
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isZoomMode) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      if (lastTouchDistance.current > 0) {
        const delta = distance / lastTouchDistance.current;
        const newScale = Math.min(Math.max(scale * delta, 1), 5);
        setScale(newScale);
      }
      lastTouchDistance.current = distance;
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    lastTouchDistance.current = 0;
  };

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      {!isZoomMode && (
        <div style={{
          ...styles.header,
          ...(isDarkMode ? styles.headerDark : {}),
        }}>
          <button onClick={handleClose} style={{
            ...styles.closeButton,
            ...(isDarkMode ? styles.closeButtonDark : {}),
          }}>
            <X size={16} /> {t('close')}
          </button>
          <span style={styles.headerTitle}>{comic.title}</span>
          <div style={styles.headerSpacer} />
        </div>
      )}

      <button onClick={toggleZoom} style={styles.zoomButton}>
        {isZoomMode ? <X size={20} /> : <Maximize2 size={20} />}
      </button>

      <div
        style={{
          ...styles.imageContainer,
          ...(isZoomMode ? styles.imageContainerZoom : {}),
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isDecrypting ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>{t('loading')}</p>
          </div>
        ) : (
          <img
            ref={imageRef}
            src={decryptedImageUrl}
            alt={`${comic.title} - Page ${currentPage + 1}`}
            style={{
              ...styles.image,
              ...(isZoomMode
                ? {
                    transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                  }
                : {
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }),
            }}
          />
        )}
      </div>

      {!isZoomMode && (
        <div style={{
          ...styles.navigation,
          ...(isDarkMode ? styles.navigationDark : {}),
        }}>
          <button
            onClick={handlePrevious}
            disabled={currentPage === 0}
            style={{
              ...styles.navButton,
              ...(currentPage === 0 ? styles.navButtonDisabled : {}),
            }}
          >
            {t('prev')}
          </button>
          <span style={styles.pageIndicator}>
            {currentPage + 1} / {pages.length}
          </span>
          <button
            onClick={handleNext}
            disabled={currentPage === pages.length - 1}
            style={{
              ...styles.navButton,
              ...(currentPage === pages.length - 1 ? styles.navButtonDisabled : {}),
            }}
          >
            {t('next')}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    height: '100vh',
    width: '100vw',
    backgroundImage: 'url(/wallpaper.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    overflow: 'hidden',
  },
  containerDark: {
    backgroundImage: 'none',
    backgroundColor: '#000000',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'linear-gradient(180deg, rgba(138, 43, 226, 0.95) 0%, rgba(138, 43, 226, 0.8) 100%)',
    zIndex: 10,
    boxShadow: '0 2px 12px rgba(138, 43, 226, 0.4)',
  },
  headerDark: {
    background: '#000000',
    borderBottom: '1px solid #333',
    boxShadow: 'none',
  },
  closeButton: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  closeButtonDark: {
    background: '#ffffff',
    color: '#000000',
    border: '2px solid #ffffff',
  },
  imageContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '70px 0 100px',
    overflow: 'hidden',
    touchAction: 'auto',
  },
  imageContainerZoom: {
    padding: 0,
    touchAction: 'none',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  zoomButton: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '50%',
    fontSize: '20px',
    fontWeight: '600',
    cursor: 'pointer',
    zIndex: 20,
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.5)',
    transition: 'all 0.3s ease',
  },
  navigation: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    background: 'linear-gradient(180deg, rgba(138, 43, 226, 0.8) 0%, rgba(138, 43, 226, 0.95) 100%)',
    boxShadow: '0 -2px 12px rgba(138, 43, 226, 0.4)',
  },
  navigationDark: {
    background: '#111111',
    borderTop: '1px solid #333',
    boxShadow: 'none',
  },
  navButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    color: '#000',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    flex: 1,
    maxWidth: '150px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    boxShadow: '0 4px 16px rgba(255, 215, 0, 0.4)',
  },
  navButtonDisabled: {
    background: 'linear-gradient(135deg, #666 0%, #444 100%)',
    color: '#999',
    cursor: 'not-allowed',
    opacity: 0.6,
    boxShadow: 'none',
    border: '2px solid rgba(255, 255, 255, 0.2)',
  },
  backButton: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '20px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.5)',
    transition: 'all 0.3s ease',
  },
  error: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    color: 'white',
    textAlign: 'center',
    padding: '20px',
  },
  pageIndicator: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    padding: '8px 16px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '20px',
  },
  headerTitle: {
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  headerSpacer: {
    width: '100px',
  },
  errorIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#8B5CF6',
    margin: '0 0 12px 0',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  },
  errorText: {
    fontSize: '16px',
    color: '#ccc',
    margin: '0 0 20px 0',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(139, 92, 246, 0.3)',
    borderTop: '4px solid #8B5CF6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
  },
};
